import { isString } from 'lodash';
import Router, { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Spinner } from '@air/primitive-spinner';
import { useSysadmin } from '@air/sysadmin';

import { getLayout } from '~/components/Layouts/HomeLayout/HomeLayout';
import { MAIN_CONTENT_ID } from '~/components/v2/layouts/SkipToMainContentLink';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { useLocationDidChange } from '~/hooks/router/useLocationDidChange';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { useWorkspaceRedirectState } from '~/hooks/useWorkspaceRedirectState';
import { workspaceIdQueryParamSelector } from '~/store/router/selectors';
import { useGetDefaultBoard } from '~/swr-hooks/boards/useGetDefaultBoard';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

const Home = () => {
  const { goToBoardPage } = useGoToPrivateBoardPage();
  const { data: workspaces } = useWorkspaces();
  const { workspaceRedirectState } = useWorkspaceRedirectState();
  const { data: board, error: boardError } = useGetDefaultBoard();
  const { locationDidChange } = useLocationDidChange('/home');
  const wsidParam = useSelector(workspaceIdQueryParamSelector);
  const { query } = useRouter();
  const { isInternal } = useSysadmin();
  const shouldShowOnboarding = isString(query.showOnboarding) && query.showOnboarding === 'true';
  const hasCanvasNameQueryParam = !!query[QueryParamNames.canvasName];
  useEffect(() => {
    if (isInternal && !hasCanvasNameQueryParam) {
      Router.push({ pathname: Routes.media.all, query: getCanvasQueryParams() });
    }
  }, [isInternal, hasCanvasNameQueryParam]);

  useEffect(() => {
    if (boardError && !hasCanvasNameQueryParam) {
      Router.replace({ pathname: Routes.media.all, query: getCanvasQueryParams() });
    }
  }, [boardError, hasCanvasNameQueryParam]);

  useEffect(() => {
    if (shouldShowOnboarding) {
      Router.push(Routes.joinApprovedWorkspace);
    }
  }, [shouldShowOnboarding]);

  useEffect(() => {
    if (
      board &&
      workspaces?.length &&
      workspaceRedirectState === 'hasAccessToWorkspace' &&
      !wsidParam &&
      !locationDidChange() &&
      !hasCanvasNameQueryParam
    ) {
      void goToBoardPage({ board, method: 'replace' });
    }
  }, [
    goToBoardPage,
    workspaceRedirectState,
    board,
    wsidParam,
    locationDidChange,
    workspaces?.length,
    hasCanvasNameQueryParam,
  ]);

  return (
    <main
      className="flex grow flex-col items-center justify-center rounded-12 ui2026:rounded-none ads:bg-base bg-gray-1"
      id={MAIN_CONTENT_ID}
      tabIndex={-1}
    >
      <Spinner className="h-7 w-8 ads:text-ads-cyan-10 text-teal-9" />
    </main>
  );
};

Home.getLayout = getLayout;

export default Home;
